import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileDown, Plus } from "lucide-react";

import { supabase } from "@/lib/supabase";
import { fetchEventById } from "@/lib/api/events";
import { fetchParticipants, type ParticipantRow } from "@/lib/api/participants";

import { Button, LinkButton } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { PageHeadline } from "@/components/ui/Headline";
import { Loader, InlineAlert } from "@/components/ui/States";
import { OrganizerShell } from "@/components/layout/OrganizerShell";

interface SurveyRow {
  relevance: number | null;
  met_someone: boolean | null;
  nps: number | null;
  comment: string | null;
}

interface MatchRow {
  id: string;
  participant_a: string;
  participant_b: string;
}

async function fetchEventSurveys(eventId: string): Promise<SurveyRow[]> {
  const { data, error } = await supabase
    .from("event_surveys")
    .select("relevance, met_someone, nps, comment")
    .eq("event_id", eventId);
  if (error) throw new Error(error.message);
  return (data ?? []) as SurveyRow[];
}

async function fetchEventMatches(eventId: string): Promise<MatchRow[]> {
  const { data, error } = await supabase
    .from("event_matches")
    .select("id, participant_a, participant_b")
    .eq("event_id", eventId);
  if (error) throw new Error(error.message);
  return (data ?? []) as MatchRow[];
}

export function FinalReportPage() {
  const { eventId } = useParams<{ eventId: string }>();

  const { data: event } = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => fetchEventById(eventId ?? ""),
    enabled: Boolean(eventId),
  });

  const { data: participants = [] } = useQuery({
    queryKey: ["participants", eventId],
    queryFn: () => fetchParticipants(eventId ?? ""),
    enabled: Boolean(eventId),
  });

  const { data: matches = [] } = useQuery({
    queryKey: ["event-matches", eventId],
    queryFn: () => fetchEventMatches(eventId ?? ""),
    enabled: Boolean(eventId),
  });

  const { data: surveys = [] } = useQuery({
    queryKey: ["event-surveys", eventId],
    queryFn: () => fetchEventSurveys(eventId ?? ""),
    enabled: Boolean(eventId),
  });

  const stats = useMemo(
    () => summarize(participants, matches, surveys),
    [participants, matches, surveys],
  );

  if (!event) {
    return (
      <OrganizerShell>
        <Loader label="Loading event..." />
      </OrganizerShell>
    );
  }

  const eventDate = new Date(event.event_date);
  const isPast = eventDate.getTime() < Date.now();

  return (
    <OrganizerShell>
      <div className="mx-auto max-w-[820px] animate-fade-in">
        <Link
          to="/events"
          className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#475569] hover:text-[#2563eb] no-print"
        >
          <ArrowLeft className="h-4 w-4" /> My Events
        </Link>

        <div className="mt-4">
          <PageHeadline
            eyebrow="Final report"
            title={event.title}
            subtitle={
              eventDate.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              }) + (!isPast ? " — preliminary, event hasn't happened yet" : "")
            }
          />
        </div>

        <Section title="Registration & Engagement">
          <Metric value={String(stats.registered)} label="registered" />
          <Metric value={`${stats.pitchConversionPct}%`} label="pitch conversion" />
          <Metric value={String(stats.approved)} label="approved pitches" />
        </Section>

        <Section title="Matching Performance">
          <Metric value={String(stats.totalMatches)} label="total matches (pairs)" />
          <Metric value={`${stats.matchRatePct}%`} label="match rate" />
          <Metric value={stats.avgMatchesPerPerson.toFixed(1)} label="avg matches per person" />
        </Section>

        <Section title="Participant Feedback">
          <Metric value={stats.nps === null ? "—" : String(stats.nps)} label="NPS" />
          <Metric
            value={stats.avgRelevance === null ? "—" : `${stats.avgRelevance.toFixed(1)} ★`}
            label="avg relevance"
          />
          <Metric
            value={stats.metSomeonePct === null ? "—" : `${stats.metSomeonePct}%`}
            label="real meetings"
          />
        </Section>

        <Card padding="md" className="mt-6">
          <h3 className="text-[16px] font-bold text-[#1a1a1a]">AI Moderation Summary</h3>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[13px] text-[#475569] sm:grid-cols-4">
            <SummaryRow label="Reviewed" value={stats.reviewed} />
            <SummaryRow label="Flagged" value={stats.flagged} tone="warning" />
            <SummaryRow label="Approved" value={stats.approved} tone="success" />
            <SummaryRow label="Rejected" value={stats.rejected} tone="danger" />
          </div>
        </Card>

        <Card padding="md" className="mt-5">
          <h3 className="text-[16px] font-bold text-[#1a1a1a]">Top goals</h3>
          {stats.topGoals.length === 0 ? (
            <p className="mt-2 text-[13px] text-[#6c757d]">No goal data captured yet.</p>
          ) : (
            <ul className="mt-3 flex flex-col gap-2 text-[14px] text-[#475569]">
              {stats.topGoals.map((g) => (
                <li key={g.term} className="flex items-center justify-between">
                  <span>{g.term}</span>
                  <Badge tone="info">{g.count} mentions</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {stats.comments.length > 0 ? (
          <Card padding="md" className="mt-5">
            <h3 className="text-[16px] font-bold text-[#1a1a1a]">Comments</h3>
            <div className="mt-3 flex flex-col gap-2">
              {stats.comments.map((c, i) => (
                <div
                  key={i}
                  className="rounded-[12px] border border-[#e2e8f0] bg-[#f8fafc] p-3 text-[14px] text-[#475569]"
                >
                  {c}
                </div>
              ))}
            </div>
          </Card>
        ) : null}

        <Card tone="approved" padding="md" className="mt-5">
          <h3 className="text-[16px] font-bold text-[#065f46]">Recommendations</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-[13px] text-[#065f46]">
            {stats.recommendations.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </Card>

        <div className="mt-8 flex flex-wrap gap-3 no-print">
          <Button
            variant="secondary"
            size="md"
            leadingIcon={<FileDown className="h-4 w-4" />}
            onClick={() => window.print()}
          >
            Download PDF Report
          </Button>
          <LinkButton
            to="/events/new"
            size="md"
            leadingIcon={<Plus className="h-4 w-4" />}
          >
            Create Next Event
          </LinkButton>
        </div>

        {!isPast ? (
          <InlineAlert tone="info" className="mt-6">
            This event hasn't happened yet — final numbers will update after it concludes.
          </InlineAlert>
        ) : null}
      </div>
    </OrganizerShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <h3 className="mb-3 text-[16px] font-bold text-[#1a1a1a]">{title}</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">{children}</div>
    </section>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <Card padding="sm">
      <p className="text-[24px] font-bold text-[#1a1a1a] leading-tight">{value}</p>
      <p className="mt-1 text-[12px] text-[#6c757d]">{label}</p>
    </Card>
  );
}

function SummaryRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "warning" | "success" | "danger";
}) {
  const valueColor =
    tone === "warning" ? "text-[#92400e]" :
    tone === "success" ? "text-[#065f46]" :
    tone === "danger" ? "text-[#991b1b]" :
    "text-[#1a1a1a]";
  return (
    <div className="rounded-[10px] bg-[#f8fafc] px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">{label}</p>
      <p className={"mt-0.5 text-[18px] font-bold " + valueColor}>{value}</p>
    </div>
  );
}

function summarize(
  participants: ParticipantRow[],
  matches: MatchRow[],
  surveys: SurveyRow[],
): {
  registered: number;
  reviewed: number;
  approved: number;
  rejected: number;
  flagged: number;
  pitchConversionPct: number;
  totalMatches: number;
  matchRatePct: number;
  avgMatchesPerPerson: number;
  nps: number | null;
  avgRelevance: number | null;
  metSomeonePct: number | null;
  topGoals: { term: string; count: number }[];
  comments: string[];
  recommendations: string[];
} {
  const registered = participants.length;
  const approved = participants.filter((p) => p.pitch_status === "approved").length;
  const rejected = participants.filter((p) => p.pitch_status === "rejected").length;
  const flagged = participants.filter((p) => p.pitch_status === "flagged").length;
  const reviewed = participants.filter((p) => p.pitch_status !== "pending").length;
  const pitchConversionPct = registered > 0 ? Math.round((reviewed / registered) * 100) : 0;

  const totalMatches = matches.length;
  const matchedParticipants = new Set<string>();
  for (const m of matches) {
    matchedParticipants.add(m.participant_a);
    matchedParticipants.add(m.participant_b);
  }
  const matchRatePct =
    registered > 0 ? Math.round((matchedParticipants.size / registered) * 100) : 0;
  const avgMatchesPerPerson = registered > 0 ? (totalMatches * 2) / registered : 0;

  const npsValues = surveys.map((s) => s.nps).filter((n): n is number => n !== null);
  const relevanceValues = surveys
    .map((s) => s.relevance)
    .filter((n): n is number => n !== null);
  const metSomeoneValues = surveys
    .map((s) => s.met_someone)
    .filter((b): b is boolean => b !== null);

  const promoters = npsValues.filter((n) => n >= 9).length;
  const detractors = npsValues.filter((n) => n <= 6).length;
  const nps =
    npsValues.length > 0
      ? Math.round(((promoters - detractors) / npsValues.length) * 100)
      : null;

  const avgRelevance =
    relevanceValues.length > 0
      ? relevanceValues.reduce((a, b) => a + b, 0) / relevanceValues.length
      : null;

  const metSomeonePct =
    metSomeoneValues.length > 0
      ? Math.round(
          (metSomeoneValues.filter(Boolean).length / metSomeoneValues.length) * 100,
        )
      : null;

  const topGoals = aggregateTopGoals(participants);

  const comments = surveys
    .map((s) => s.comment?.trim())
    .filter((c): c is string => Boolean(c && c.length > 0))
    .slice(0, 10);

  const recommendations: string[] = [];
  if (pitchConversionPct < 50 && registered > 0) {
    recommendations.push(
      "Pitch conversion is under 50% — send earlier reminders next time.",
    );
  } else if (pitchConversionPct >= 70) {
    recommendations.push("Great engagement! 70%+ pitch rate is above average.");
  }
  if (nps !== null && nps >= 50) {
    recommendations.push("Strong NPS — consider a follow-up event with the same crowd.");
  }
  if (matchRatePct < 30 && registered > 0) {
    recommendations.push(
      "Low match rate — invite a more diverse audience or refine the event description next time.",
    );
  }
  if (recommendations.length === 0) {
    recommendations.push("Keep the mechanic consistent — more data helps surface insights.");
  }

  return {
    registered,
    reviewed,
    approved,
    rejected,
    flagged,
    pitchConversionPct,
    totalMatches,
    matchRatePct,
    avgMatchesPerPerson,
    nps,
    avgRelevance,
    metSomeonePct,
    topGoals,
    comments,
    recommendations,
  };
}

const STOPWORDS = new Set([
  "the","a","an","and","or","but","for","to","of","in","on","with","at","by",
  "is","are","was","were","be","i","you","he","she","we","they","my","your",
  "this","that","as","if","so","can","will","not","no","any","from","up","out",
]);

function aggregateTopGoals(
  participants: ParticipantRow[],
): { term: string; count: number }[] {
  const counter = new Map<string, number>();
  for (const p of participants) {
    const text = [p.looking_for, p.can_help_with].filter(Boolean).join(" ").toLowerCase();
    if (!text) continue;
    const words = text.match(/[a-zа-яё0-9][a-zа-яё0-9-]{2,}/gi) ?? [];
    const seen = new Set<string>();
    for (const w of words) {
      if (STOPWORDS.has(w) || seen.has(w)) continue;
      seen.add(w);
      counter.set(w, (counter.get(w) ?? 0) + 1);
    }
  }
  return [...counter.entries()]
    .filter(([, c]) => c >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([term, count]) => ({ term, count }));
}
